import {
  Component,
  output,
  ChangeDetectorRef,
  ElementRef,
  viewChild,
  OnInit,
  AfterViewInit,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import moment from 'moment';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { GridEventService } from '../../../core/grid/services/grid-event.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { AddCommentService } from './add-comment.service';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'svg', 'pdf', 'xls', 'xlsx', 'txt', 'doc', 'docx', 'ico', 'ppt', 'pptx', 'ods', 'wpd', 'rtf'];

interface UserChip {
  NAME: string;
  VALUE: string;
}

interface TagOption {
  tag_key: number | string;
  tag_name: string;
}

@Component({
  selector: 'gtw-add-comment',
  templateUrl: './add-comment.component.html',
  styleUrls: ['./add-comment.component.scss'],
  imports: [FormsModule],
  host: {
    '(focusin)': 'onFocusIn($event)',
  },
})
export class AddCommentComponent implements OnInit, AfterViewInit {
  readonly requestGridUpdate = output<string>({ alias: 'request-grid-update' });
  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly destroyRef = inject(DestroyRef);
  private readonly gtwGlobal = inject(GlobalService);
  private readonly gtwWorkspace = inject(WorkspaceService);
  private readonly gtwApp = inject(AppService);
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly addCommentService = inject(AddCommentService);
  private readonly gridEventService = inject(GridEventService);

  private suppressInitialFocus = true;
  private isInitialized = false;

  title = 'Add Comment';
  minimise = false;
  crudLoading = false;
  tagCrudLoading = false;
  formSubmitted = false;
  isEditMode = false;
  modalName = '';

  cellData = '';
  uploadUrl = '';

  comment: any = {
    comment_key: null,
    is_public: '0',
    message: '',
    notify: [] as UserChip[],
    subject: '',
    status: '',
    attachmentList: [],
    tags: [],
  };

  actionItem: any = {
    tags: [] as TagOption[],
    action_tag: '',
    assign: [] as UserChip[],
    target_date: '',
    action_item: 0,
    review_meeting: 0,
  };

  tag: any = { tag_name: '', tag_desc: '' };
  tags: TagOption[] = [];
  isShowTag = false;
  tagPanelOpen = false;

  notifyUserSearch = '';
  assignUserSearch = '';
  notifyUserResults: any[] = [];
  assignUserResults: any[] = [];
  notifyUserLoading = false;
  assignUserLoading = false;
  notifyNoResults = false;
  assignNoResults = false;

  attachmentTO = {
    attachmented_files: [] as string[],
    attachment_file: [] as string[],
    stored_files: [] as string[],
    isError: false,
    errorMessage: '',
  };
  uploadingFiles: { name: string; progress: number }[] = [];

  gridData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};

  onFocusIn(event: FocusEvent): void {
    if (this.suppressInitialFocus) {
      (event.target as HTMLElement)?.blur();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => { this.suppressInitialFocus = false; }, 500);
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.gtwWorkspace.setTabActive.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isInitialized = false;
      this.applyWorkspaceContext();
      this.tryInit();
    });
    this.gtwGlobal.globalParamsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isInitialized = false;
      this.applyWorkspaceContext();
      this.tryInit();
    });
    this.tryInit();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.uploadUrl = this.addCommentService.getUploadUrl(this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '');

    const gridPayload = activeScreen?.data?.gridData || activeScreen?.data?.grid_data;
    if (gridPayload && Object.keys(gridPayload).length > 0) {
      this.gridData = gridPayload;
    }

    const modalName = activeScreen?.data?.modalName || activeScreen?.data?.modal_name || '';
    if (modalName) {
      this.modalName = modalName;
      this.isEditMode = modalName.toLowerCase().includes('edit');
      this.title = this.isEditMode ? 'Edit Comment' : 'Add Comment';
    }
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url && !this.baseURLs?.api) {
      return;
    }
    if (!this.gridData || Object.keys(this.gridData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    if (this.gridData?.selected?.value != null) {
      this.cellData = this.gridData.selected.value;
    } else if (!this.gridData.selected) {
      this.gridData.selected = {};
    }

    this.loadTags();

    if (this.isEditMode && this.gridData?.comment_key) {
      this.loadComment(this.gridData.comment_key);
    }

    this.cdr.detectChanges();
  }

  private loadTags(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    this.addCommentService.getTagsList(baseUrl).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1') {
          this.tags = data.tags || [];
          // The comment may have loaded first, leaving its chips without names.
          this.actionItem.tags = this.normalizeTags(this.actionItem.tags);
          this.cdr.detectChanges();
        }
      },
      () => {
        this.snackBar.open('Error loading tags', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
      },
    );
  }

  private loadComment(commentKey: any): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    this.addCommentService.commentCrud(`${baseUrl}/commentByKey`, { comment_key: commentKey }).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1' && data.comment) {
          this.comment = { ...this.comment, ...data.comment };
          this.cellData = data.comment.value || '';
          this.comment.notify = (data.comment.notifyList || []).map((value: any) => ({
            NAME: `${value.display_name} - (${value.sso_id})`,
            VALUE: value.sso_id,
          }));
          this.actionItem.assign = (data.comment.assignList || []).map((value: any) => ({
            NAME: `${value.display_name} - (${value.sso_id})`,
            VALUE: value.sso_id,
          }));
          this.comment.subject = data.comment.subject;
          this.actionItem.target_date = data.comment.task_target_date
            ? moment(data.comment.task_target_date).format('YYYY-MM-DD')
            : '';
          this.actionItem.action_item = this.resolveActionItem(data.comment);
          this.actionItem.tags = this.normalizeTags(
            data.comment.tags ?? data.comment.tagList ?? data.comment.tagsList ?? data.comment.commentTags,
          );
          this.comment.is_public = String(data.comment.is_public ?? '0');
          this.attachmentTO.attachmented_files = [];
          this.attachmentTO.stored_files = [];
          if (data.comment.attachmentList?.length) {
            for (const attachment of data.comment.attachmentList) {
              const rawName = attachment.file_name.replace(`${data.comment.comment_key}//`, '');
              this.attachmentTO.attachmented_files.push(this.getAttachmentDisplayName(rawName));
            }
            this.attachmentTO.attachmented_files = _.uniq(this.attachmentTO.attachmented_files);
            this.attachmentTO.stored_files = _.cloneDeep(this.attachmentTO.attachmented_files);
            this.comment.attachmentList = data.comment.attachmentList;
          }
        } else {
          this.snackBar.open('Error loading comment', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        }
        this.cdr.detectChanges();
      },
      () => {
        this.snackBar.open('Error loading comment', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
      },
    );
  }

  /**
   * Saved tags come back as an array, as the JSON string we posted, or as a CSV
   * of keys; entries are either objects or bare keys. Chips need a name to show.
   */
  private normalizeTags(rawTags: unknown): TagOption[] {
    let entries: unknown = rawTags;

    if (typeof entries === 'string') {
      const text = entries.trim();
      if (!text) {
        return [];
      }
      try {
        entries = JSON.parse(text) as unknown;
      } catch {
        entries = text.split(',');
      }
    }

    if (entries && !Array.isArray(entries) && typeof entries === 'object') {
      entries = [entries];
    }

    if (!Array.isArray(entries)) {
      return [];
    }

    const normalized: TagOption[] = [];
    entries.forEach((entry: unknown) => {
      const isObject = entry !== null && typeof entry === 'object';
      const record = isObject ? (entry as Record<string, unknown>) : null;
      const rawKey = record ? record['tag_key'] ?? record['TAG_KEY'] : entry;
      const tagKey = typeof rawKey === 'string' ? rawKey.trim() : rawKey;
      if (tagKey === undefined || tagKey === null || tagKey === '') {
        return;
      }
      const tagName =
        this.tags.find((t) => String(t.tag_key) === String(tagKey))?.tag_name ||
        (record ? record['tag_name'] ?? record['TAG_NAME'] : null) ||
        String(tagKey);
      if (!normalized.some((t) => String(t.tag_key) === String(tagKey))) {
        normalized.push({ tag_key: tagKey as string | number, tag_name: String(tagName) });
      }
    });
    return normalized;
  }

  /**
   * `commentByKey` returns `task_action_item` as null even for saved action items,
   * so fall back to the task record it does return (status/target date/assignees).
   */
  private resolveActionItem(comment: Record<string, unknown>): number {
    if (this.toActionFlag(comment['task_action_item'])) {
      return 1;
    }
    const assignList = comment['assignList'];
    const hasTask =
      !!String(comment['task_status'] ?? '').trim() ||
      !!String(comment['task_target_date'] ?? '').trim() ||
      (Array.isArray(assignList) && assignList.length > 0);
    return hasTask ? 1 : 0;
  }

  /** The payload sends this flag as 1/'1', 'Y' or true depending on the caller. */
  private toActionFlag(value: unknown): number {
    if (value === true) {
      return 1;
    }
    const normalized = String(value ?? '').trim().toUpperCase();
    return normalized === '1' || normalized === 'Y' || normalized === 'TRUE' ? 1 : 0;
  }

  reviewMeeting(): void {
    if (this.actionItem.review_meeting == 1) {
      this.comment.is_public = '1';
    } else {
      this.comment.is_public = '0';
    }
    this.cdr.detectChanges();
  }

  toggleTagPanel(): void {
    this.isShowTag = !this.isShowTag;
    this.tagPanelOpen = this.isShowTag;
    this.cdr.detectChanges();
  }

  tagCancel(): void {
    this.isShowTag = false;
    this.tagPanelOpen = false;
    this.cdr.detectChanges();
  }

  saveTag(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    this.tagCrudLoading = true;
    this.addCommentService.saveTag(baseUrl, this.tag.tag_name, this.tag.tag_desc).subscribe(
      (data: any) => {
        this.tagCrudLoading = false;
        if (data?.callSuccess === '1') {
          this.snackBar.open('Tag saved successfully', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
          this.loadTags();
          this.tag.tag_name = '';
          this.tag.tag_desc = '';
          this.tagCancel();
        } else {
          this.snackBar.open('Error saving tag', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        }
        this.cdr.detectChanges();
      },
      () => {
        this.tagCrudLoading = false;
        this.snackBar.open('Error saving tag', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        this.cdr.detectChanges();
      },
    );
  }

  updateTagsTo(): void {
    const selected = this.tags.find((t) => String(t.tag_key) === String(this.actionItem.action_tag));
    if (selected && !this.actionItem.tags.some((t: TagOption) => String(t.tag_key) === String(selected.tag_key))) {
      this.actionItem.tags.push(selected);
    }
    this.actionItem.action_tag = '';
    this.cdr.detectChanges();
  }

  removeTag(index: number): void {
    this.actionItem.tags.splice(index, 1);
    this.cdr.detectChanges();
  }

  onNotifyUserSearch(event: Event): void {
    this.searchUsers(event, 'notify');
  }

  onAssignUserSearch(event: Event): void {
    this.searchUsers(event, 'assign');
  }

  private searchUsers(event: Event, mode: 'notify' | 'assign'): void {
    const val = (event.target as HTMLInputElement).value;
    if (mode === 'notify') {
      this.notifyUserSearch = val;
    } else {
      this.assignUserSearch = val;
    }
    if (!val || val.length < 2) {
      if (mode === 'notify') {
        this.notifyUserResults = [];
        this.notifyNoResults = false;
      } else {
        this.assignUserResults = [];
        this.assignNoResults = false;
      }
      this.cdr.detectChanges();
      return;
    }

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    if (mode === 'notify') {
      this.notifyUserLoading = true;
    } else {
      this.assignUserLoading = true;
    }

    this.addCommentService.searchUsers(baseUrl, val).subscribe(
      (data: any) => {
        const results = data?.jsonObject || [];
        if (mode === 'notify') {
          this.notifyUserResults = results;
          this.notifyNoResults = results.length === 0;
          this.notifyUserLoading = false;
        } else {
          this.assignUserResults = results;
          this.assignNoResults = results.length === 0;
          this.assignUserLoading = false;
        }
        this.cdr.detectChanges();
      },
      () => {
        if (mode === 'notify') {
          this.notifyUserResults = [];
          this.notifyUserLoading = false;
        } else {
          this.assignUserResults = [];
          this.assignUserLoading = false;
        }
        this.cdr.detectChanges();
      },
    );
  }

  selectNotifyUser(user: any): void {
    this.addUserChip(user, this.comment.notify);
    this.notifyUserSearch = '';
    this.notifyUserResults = [];
    this.cdr.detectChanges();
  }

  selectAssignUser(user: any): void {
    this.addUserChip(user, this.actionItem.assign);
    this.assignUserSearch = '';
    this.assignUserResults = [];
    this.cdr.detectChanges();
  }

  private addUserChip(user: any, target: UserChip[]): void {
    const value = user.sso_id || user.VALUE || user.value;
    if (target.some((u) => u.VALUE === value)) {
      return;
    }
    const display = user.display_name || user.NAME || user.name || value;
    target.push({ NAME: `${display} - (${value})`, VALUE: value });
  }

  removeUser(index: number): void {
    this.comment.notify.splice(index, 1);
    this.cdr.detectChanges();
  }

  removeAssignUser(index: number): void {
    this.actionItem.assign.splice(index, 1);
    this.cdr.detectChanges();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) {
      return;
    }
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (this.isValidFile(file)) {
        this.uploadFile(file);
      }
    }
    const fileInput = this.fileInput();
    if (fileInput) {
      fileInput.nativeElement.value = '';
    }
  }

  triggerFileInput(): void {
    this.fileInput()?.nativeElement?.click();
  }

  private isValidFile(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      this.attachmentTO.isError = true;
      this.attachmentTO.errorMessage = `Invalid file type: ${extension}`;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.attachmentTO.isError = false;
        this.cdr.detectChanges();
      }, 5000);
      return false;
    }
    return true;
  }

  private uploadFile(file: File): void {
    const uploadingFile = { name: file.name, progress: 0 };
    this.uploadingFiles.push(uploadingFile);
    this.cdr.detectChanges();

    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9\s.]/gi, '_');
    const flowIdentifier = `${file.size}-${sanitizedFileName.replace(/[^a-zA-Z0-9]/g, '')}`;
    const formData = new FormData();
    formData.append('flowChunkNumber', '1');
    formData.append('flowChunkSize', '104857600');
    formData.append('flowCurrentChunkSize', file.size.toString());
    formData.append('flowTotalSize', file.size.toString());
    formData.append('flowIdentifier', flowIdentifier);
    formData.append('flowFilename', sanitizedFileName);
    formData.append('flowRelativePath', sanitizedFileName);
    formData.append('flowTotalChunks', '1');
    formData.append('file', file, sanitizedFileName);

    this.http.post(this.uploadUrl, formData, { reportProgress: true, observe: 'events', withCredentials: true }).subscribe(
      (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          uploadingFile.progress = Math.round(100 * (event.loaded || 0) / (event.total || 1));
          this.cdr.detectChanges();
        } else if (event.type === HttpEventType.Response) {
          const idx = this.uploadingFiles.indexOf(uploadingFile);
          if (idx > -1) {
            this.uploadingFiles.splice(idx, 1);
          }
          const response: any = event.body;
          if (response?.uploadStatus === 'success' || response?.callSuccess === '1' || response?.callSuccess === 1) {
            const serverFileName = response.flowFilename || sanitizedFileName;
            // Display: original filename only (legacy uses flow.files[].name).
            // Save payload: server flowFilename (may include temp path).
            this.attachmentTO.attachmented_files.push(sanitizedFileName);
            this.attachmentTO.attachment_file.push(serverFileName);
          } else {
            this.snackBar.open(response?.errorMessage || 'Upload failed', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
          }
          this.cdr.detectChanges();
        }
      },
      () => {
        const idx = this.uploadingFiles.indexOf(uploadingFile);
        if (idx > -1) {
          this.uploadingFiles.splice(idx, 1);
        }
        this.snackBar.open('Network error during upload', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        this.cdr.detectChanges();
      },
    );
  }

  removeAttachment(file: string): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    const fileName = this.getAttachmentDisplayName(file);
    this.addCommentService.removeAttachment(baseUrl, fileName).subscribe(
      () => {
        this.attachmentTO.attachmented_files = this.attachmentTO.attachmented_files.filter((f) => f !== file);
        this.attachmentTO.attachment_file = this.attachmentTO.attachment_file.filter((f) => f !== file);
        this.cdr.detectChanges();
      },
      () => {
        this.attachmentTO.attachmented_files = this.attachmentTO.attachmented_files.filter((f) => f !== file);
        this.attachmentTO.attachment_file = this.attachmentTO.attachment_file.filter((f) => f !== file);
        this.cdr.detectChanges();
      },
    );
  }

  save(): void {
    this.formSubmitted = true;
    if (!this.comment.subject?.trim() || !this.comment.message?.trim()) {
      this.snackBar.open('Please correct the errors below', 'Close', { duration: 4000, panelClass: ['warning-snackbar'] });
      this.cdr.detectChanges();
      return;
    }
    if (this.crudLoading) {
      this.snackBar.open('Please wait while we save this request', 'Close', { duration: 4000, panelClass: ['info-snackbar'] });
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    if (this.isEditMode) {
      this.saveEdit();
    } else {
      this.saveAdd();
    }
  }

  private saveAdd(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const filterCached = activeScreen?.filters?.getFilterParamsFromCache
      ? activeScreen.filters.getFilterParamsFromCache()
      : {};
    const screenKey = activeScreen?.screen_key || '-100';
    const activityKey = activeScreen?.activity_key || '-102';

    let row_key = '';
    let col_key = '';
    let tableState: any = {};
    let element = '';
    if (this.gridData?.selected) {
      row_key = this.gridData.selected.row_id;
      col_key = this.gridData.selected.col_id;
      tableState = this.gridData.tableState || {};
      element = this.gridData.selected.select_type;
    }

    let action_template_key: any[] = [];
    if (activeScreen?.data?.actionTemplates) {
      action_template_key = _.filter(activeScreen.data.actionTemplates, { selectedAction: true });
    }
    if (action_template_key?.length > 0) {
      tableState.action_template_key = action_template_key[0].action_template_key;
    }

    const notify = _.map(this.comment.notify, 'VALUE').toString();
    const assign = _.map(this.actionItem.assign, 'VALUE').toString();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';

    const objContext: any = {
      state: 'messages',
      filters: JSON.stringify(filterCached),
      tableState: JSON.stringify(tableState),
      data: JSON.stringify(_.omit(this.gridData.selected, 'element')),
      type: this.gridData?.type != null ? this.gridData.type : 'grid',
      screen_key: screenKey,
      activity_key: activityKey,
      row_key,
      col_key,
      element,
      comment_key: this.comment.comment_key,
      is_public: this.comment.is_public,
      message: this.comment.message,
      notify,
      attachments: this.attachmentTO.attachment_file.toString(),
      tags: JSON.stringify(this.actionItem.tags),
      follow_up: this.comment.follow_up || 0,
      follow_up_by: this.comment.follow_up_by,
      value: this.cellData,
      subject: this.comment.subject,
      task_action_item: this.actionItem.action_item ? this.actionItem.action_item : 0,
      task_target_date: this.actionItem.target_date
        ? moment(this.actionItem.target_date).format('YYYY/MM/DD')
        : '',
      assign,
    };

    this.addCommentService.commentCrud(`${baseUrl}/addComment`, objContext).subscribe(
      (data: any) => this.handleSaveResponse(data),
      () => this.handleSaveError(),
    );
  }

  private saveEdit(): void {
    const notify = _.map(this.comment.notify, 'VALUE').toString();
    const assign = _.map(this.actionItem.assign, 'VALUE').toString();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    const oldFiles: any[] = [];

    if (!_.isEqual(this.attachmentTO.attachmented_files.length, this.comment.attachmentList?.length || 0)) {
      const removedList = (this.comment.attachmentList || []).filter(
        (item: any) => !this.attachmentTO.attachmented_files.includes(this.getAttachmentDisplayName(item.file_name)),
      );
      removedList.forEach((item: any) => {
        oldFiles.push({
          parent_key: this.comment.comment_key,
          parent_type: this.comment.type,
          doc_key: item.attachment_key,
          file_name: item.file_name,
        });
      });
    }

    const objContext: any = {
      comment_key: this.comment.comment_key,
      is_public: this.comment.is_public,
      message: this.comment.message,
      notify,
      attachments: this.attachmentTO.attachment_file.toString(),
      tags: JSON.stringify(this.actionItem.tags),
      oldFiles: JSON.stringify(oldFiles),
      task_action_item: this.actionItem.action_item,
      task_target_date: this.actionItem.target_date
        ? moment(this.actionItem.target_date).format('YYYY/MM/DD')
        : '',
      assign,
      status: this.comment.status,
      subject: this.comment.subject,
      type: this.gridData?.type != null ? this.gridData.type : 'grid',
      row_key: this.gridData?.row_key,
    };

    this.addCommentService.commentCrud(`${baseUrl}/updateComment`, objContext).subscribe(
      (data: any) => this.handleSaveResponse(data),
      () => this.handleSaveError(),
    );
  }

  private handleSaveResponse(data: any): void {
    this.crudLoading = false;
    if (data?.callSuccess === '1') {
      const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
      const commentType = activeScreen?.data?.settings?.commentType || 'all';
      this.gridEventService.broadcast('loadGridMessages', commentType, true);
      this.attachmentTO.attachmented_files = [];
      this.requestGridUpdate.emit('refresh');
    } else {
      this.snackBar.open('Error occurred while saving', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
    }
    this.cdr.detectChanges();
  }

  private handleSaveError(): void {
    this.crudLoading = false;
    this.snackBar.open('Error occurred while saving', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  getAttachmentDisplayName(filePath: string): string {
    if (!filePath) {
      return '';
    }
    const normalized = String(filePath).replace(/\\/g, '/');
    return normalized.split('/').pop() || filePath;
  }
}
